import { decodeB64WithEncoding } from '../../../shared/api/base64/decodeB64WithEncoding.js';
import { redirectWithGet } from '../../../shared/api/redirect/redirect.js';
import { executeFinish } from './executeFinish.js';
import { executeStep } from './executeStep.js';
import { getSteps } from './getSteps.js';
import { prepareStep } from './prepareStep.js';
import { focusFirst } from '@edicom/common/element/focusFirst';

export class ViewEnrollment extends ViewPromiseController {
  constructor() {
    super({
      name: 'ViewEnrollment',
      style: `
        :host .wizard {
          height: 100%;
          padding: var(--ewc-padding-big);
        }`,
      promise: {
        getData: getSteps,
        template: `<div class="wizard"></div>`,
      },
    });
  }

  static get [ViewPromiseController.$properties]() {
    return {
      user: { type: 'String' },
      domain: { type: 'String' },
      group: { type: 'String' },
      token: { type: 'String' },
      data: { type: 'Array', attribute: false },
    };
  }

  get eTemplate() {
    return this.shadowRoot.querySelector('.wizard');
  }

  [ViewPromiseController.$onResolved](data) {
    this.data = data;
    this.render();
  }

  [ViewPromiseController.$onRejected](error) {
    const message = error?.message || 'global.warningMsg';
    redirectWithGet(`/alert/error?title=enrollment.title&message=${message}`);
  }

  render() {
    const eTemplate = this.shadowRoot.querySelector('.wizard');
    eTemplate.innerHTML = `
      <ewc-wizard no-prev>
        ${this.data.map((step) => step.template).join('')}
      </ewc-wizard>
    `;

    // Registro de eventos
    const eWizard = eTemplate.querySelector('ewc-wizard');

    const onVisible = this.#onVisible.bind(this);
    const onBeforeNextStep = this.#onBeforeNextStep.bind(this);
    const onFinish = this.#onFinish.bind(this);

    eWizard.addEventListener('EwcWizard:visible', function () {
      onVisible(this);
    });
    eWizard.addEventListener('EwcWizard:step', function () {
      onBeforeNextStep(this);
    });
    eWizard.addEventListener('EwcWizard:finish', function () {
      onFinish(this);
    });
  }

  /*
    Para los tres eventos siempre consultamos si hay que ejecutar algo personalizado:
    events.visible, events.step (Pasamos el eWizard para dar opción de buscar en el template)
  */
  #onVisible(eWizard) {
    eWizard.loading = true;
    const step = eWizard.current;
    const stepNum = this.data[step].step;
    const events = this.#getEventsFromStep(step);

    prepareStep(this.token, this.user, stepNum)
      .then((responseData) => {
        this.data[step].data = responseData; // Lo necesitaremos para el execute
        return events?.visible ? events.visible(eWizard, responseData, this.data[step].params || {}, { token: this.token, user: this.user, stepNum }) : responseData;
      })
      .catch(() => {
        this.data[step].data = {};
      })
      .finally(() => {
        eWizard.loading = false;
        stepNum !== 9 && focusFirst(eWizard);
      });
  }

  #onBeforeNextStep(eWizard) {
    const step = eWizard.current;
    const events = this.#getEventsFromStep(step);
    if (events?.step) {
      // Bind access private field
      const execStep = this.#executeStep.bind(this);
      const resolve = function (stepData) {
        execStep(eWizard, stepData);
      };
      const reject = function () {
        eWizard.reject();
      };

      events.step(eWizard, this.data[step].data, resolve, reject);
    } else {
      this.#executeStep(eWizard);
    }
  }

  #onFinish(eWizard) {
    const step = eWizard.current;
    const events = this.#getEventsFromStep(step);
    if (events?.step) {
      // Bind access private field
      const execFinish = this.#executeFinish.bind(this);
      const resolve = function (stepData) {
        execFinish(eWizard, stepData);
      };
      const reject = function () {
        eWizard.eBtnFinish.state = 'rejected';
      };

      events.step(eWizard, this.data[step].data, resolve, reject);
    } else {
      this.#executeFinish(eWizard);
    }
  }

  #getEventsFromStep(step) {
    return this.data[step].events;
  }

  #executeStep(eWizard, stepData) {
    const stepNum = this.data[eWizard.current].step;
    executeStep(this.token, this.user, stepNum, stepData || {})
      // Envolvemos con una función para que las funciones del wizard no pierdan la referencia this
      .then(
        (res) => {
          eWizard.resolve();
          this.#replaceToken(res);
        },
        () => eWizard.reject()
      );
  }

  #replaceToken(res) {
    if (res?.outputData?.token) {
      this.token = decodeB64WithEncoding(res.outputData.token);
    }
  }

  #executeFinish(eWizard, stepData) {
    const stepNum = this.data[eWizard.current].step;
    executeFinish(this.token, this.user, this.domain, this.group, stepNum, stepData || {}).then(
      () => {
        // Con retardo por el check ok como en el login
        setTimeout(() => {
          eWizard.eBtnFinish.state = 'resolved';
        }, 1000);
      },
      () => {
        eWizard.eBtnFinish.state = 'rejected';
      }
    );
  }
}
